"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import io, { Socket } from "socket.io-client";
import { getAuthHeaders, getWebSocketUrl } from "@/app/services/api.utils";
import { NotificationPayload } from "@/app/lib/models/NotificationModel";
import { usePathname } from "next/navigation";
import logger from "@/lib/logger";

interface UseUserWebSocketOptions {
  userId?: string;
  emailId: string;
  onNotification?: (notification: NotificationPayload) => void;
  enabled?: boolean;
  currentPage?: string; // Optional: current page/route for tracking
  deviceType?: string; // Optional: device type (mobile, desktop, etc.)
}

export const useUserWebSocket = ({
  userId,
  emailId,
  onNotification,
  enabled = true,
  currentPage,
  deviceType,
}: UseUserWebSocketOptions) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketId, setSocketId] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const onNotificationRef = useRef(onNotification);
  const connectionIdRef = useRef<string | null>(null);
  const pathname = usePathname();

  // Use provided currentPage or fall back to pathname
  const effectiveCurrentPage = currentPage || pathname;

  // Update notification callback ref
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  const webSocketUrl = useMemo(() => getWebSocketUrl(), []);

  useEffect(() => {
    // Don't connect if disabled or missing required params
    if (!enabled || !emailId || emailId.trim() === "") {
      return;
    }

    // Note: userId is optional - backend will look it up from emailId if not provided
    // Only require emailId for connection
    if (!emailId) {
      logger.info("Skipping WebSocket connection - missing emailId");
      return;
    }

    // Check if we already have a socket with same parameters
    const paramsMatch =
      socketRef.current !== null &&
      socketRef.current.connected &&
      isConnectingRef.current === false;

    if (paramsMatch) {
      logger.info("WebSocket already connected, skipping new connection");
      return;
    }

    // If we have an existing socket with different parameters, disconnect it first
    if (socketRef.current) {
      logger.info("Disconnecting existing socket before creating new one");
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      isConnectingRef.current = false;
    }

    // Create unique connection ID
    const connectionId = `${userId || emailId}-${Date.now()}-${Math.random()}`;
    connectionIdRef.current = connectionId;
    isConnectingRef.current = true;

    const connectSocket = async () => {
      try {
        const headers = await getAuthHeaders(undefined, emailId);
        const token = headers["Authorization"];

        if (!token) {
          logger.warn("No auth token available, skipping WebSocket connection");
          isConnectingRef.current = false;
          return;
        }

        const fullWebSocketUrl = `${webSocketUrl}/notifications`;
        logger.info(`Attempting WebSocket connection to: ${fullWebSocketUrl}`);

        // Check if connection was cancelled
        if (connectionIdRef.current !== connectionId) {
          logger.info("Connection cancelled before socket creation");
          return;
        }

        const authConfig: any = {
          emailId: emailId,
          token: token,
        };

        // Add userId if available (optional - backend will look it up if missing)
        if (userId && userId.trim() !== "") {
          authConfig.userId = userId;
          logger.info(`Connecting with userId: ${userId}, emailId: ${emailId}`);
        } else {
          logger.info(
            `Connecting without userId - backend will look it up from emailId: ${emailId}`,
          );
        }

        // Add metadata for tracking multiple tabs/devices
        if (effectiveCurrentPage) {
          authConfig.currentPage = effectiveCurrentPage;
        }
        if (deviceType) {
          authConfig.deviceType = deviceType;
        } else {
          // Auto-detect device type
          const isMobile =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent,
            );
          authConfig.deviceType = isMobile ? "mobile" : "desktop";
        }

        const socketConnection = io(fullWebSocketUrl, {
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
          forceNew: true,
          auth: authConfig,
          upgrade: true,
          rememberUpgrade: true,
        });

        // Check again after socket creation
        if (connectionIdRef.current !== connectionId) {
          logger.info("Connection cancelled after socket creation");
          socketConnection.disconnect();
          return;
        }

        socketRef.current = socketConnection;
        setSocket(socketConnection);

        socketConnection.on("connect", () => {
          if (
            connectionIdRef.current === connectionId &&
            socketRef.current === socketConnection
          ) {
            const connectedSocketId = socketConnection.id || null;
            logger.info(
              `User WebSocket connected: ${connectedSocketId} (userId: ${userId || "N/A"}, emailId: ${emailId})`,
            );
            if (connectedSocketId) {
              setSocketId(connectedSocketId);
            }
            isConnectingRef.current = false;
          } else {
            logger.info(
              "Connection completed but was superseded - disconnecting",
            );
            socketConnection.disconnect();
          }
        });

        socketConnection.on(
          "connected",
          (data: {
            userId?: string;
            emailId?: string;
            socketId?: string;
            totalConnections?: number;
          }) => {
            logger.info("WebSocket connection confirmed:", data);
            if (data.socketId) {
              setSocketId(data.socketId);
            } else if (socketConnection.id) {
              // Fallback to socket connection ID if not provided in data
              setSocketId(socketConnection.id);
            }
            if (data.totalConnections && data.totalConnections > 1) {
              logger.info(
                `User has ${data.totalConnections} active connections (multiple tabs/devices)`,
              );
            }
          },
        );

        socketConnection.on("reconnect", (attemptNumber: number) => {
          logger.info(
            `User WebSocket reconnected after ${attemptNumber} attempts`,
          );
        });

        socketConnection.on("reconnect_attempt", (attemptNumber: number) => {
          logger.info(`User WebSocket reconnection attempt ${attemptNumber}`);
        });

        socketConnection.on("reconnect_failed", () => {
          logger.error("User WebSocket reconnection failed after all attempts");
          isConnectingRef.current = false;
        });

        socketConnection.on("disconnect", () => {
          logger.info(`User WebSocket disconnected: ${socketConnection.id}`);
          setSocketId(null);
          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
          }
          isConnectingRef.current = false;
        });

        socketConnection.on(
          "notification",
          (notification: NotificationPayload) => {
            logger.info("Received notification:", notification);
            if (onNotificationRef.current) {
              onNotificationRef.current(notification);
            }
          },
        );

        socketConnection.on("connect_error", (error: any) => {
          if (connectionIdRef.current === connectionId) {
            logger.error("User WebSocket connection error:", error);
            logger.error("Connection error details:", {
              message: error?.message || "Unknown error",
              type: error?.type,
              description: error?.description,
              context: error?.context,
              userId,
              emailId,
              webSocketUrl: `${webSocketUrl}/notifications`,
            });
            isConnectingRef.current = false;
          }
        });

        // Periodic keepalive
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
        }
        keepAliveIntervalRef.current = setInterval(() => {
          if (socketConnection.connected) {
            socketConnection.emit("keepalive", { timestamp: Date.now() });
          }
        }, 30000); // Every 30 seconds
      } catch (error) {
        logger.error("Error connecting user WebSocket:", error);
        isConnectingRef.current = false;
      }
    };

    connectSocket();

    // Cleanup
    return () => {
      if (connectionIdRef.current === connectionId) {
        connectionIdRef.current = null;
      }
      isConnectingRef.current = false;

      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }

      // Only disconnect if params changed or component unmounting
      if (socketRef.current) {
        logger.info("Cleaning up user WebSocket connection");
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    };
  }, [
    userId,
    emailId,
    enabled,
    webSocketUrl,
    effectiveCurrentPage,
    deviceType,
  ]);

  // Update socket metadata when page changes
  useEffect(() => {
    if (socket && socket.connected && socketId && effectiveCurrentPage) {
      // Emit metadata update to backend
      socket.emit("update-metadata", {
        socketId: socketId,
        currentPage: effectiveCurrentPage,
        deviceType:
          deviceType ||
          (navigator.userAgent.match(/Mobile/) ? "mobile" : "desktop"),
      });
    }
  }, [socket, socketId, effectiveCurrentPage, deviceType]);

  return {
    socket,
    socketId, // Expose socket ID so it can be used to send targeted notifications
    isConnected: socket?.connected || false,
    isConnecting: isConnectingRef.current,
  };
};

export default useUserWebSocket;
